import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, HStack, Input, Spinner, Stack, VStack } from "@chakra-ui/react"
import { Button, Text, toaster } from "@/components"
import { getFmvStaging, getFmvStagingPreview, confirmFmvStaging } from "@/api/fmvStaging"
import { searchCompaniesByName } from "@/api/companies"
import type { FmvStagingRecord } from "@/types/company"

export const FmvStagingComponent = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [staging, setStaging] = useState<FmvStagingRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)

  // Editable fields
  const [companyId, setCompanyId] = useState<number | null>(null)
  const [companyName, setCompanyName] = useState("")
  const [fmvValue, setFmvValue] = useState("")
  const [fmvDate, setFmvDate] = useState("")
  const [fmvSource, setFmvSource] = useState("")
  const [fileLabel, setFileLabel] = useState("")
  const [folderPath, setFolderPath] = useState("")

  // Company search
  const [companySearch, setCompanySearch] = useState("")
  const [companyResults, setCompanyResults] = useState<Array<{ id: number; name: string; folderPath?: string | null }>>([])
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false)

  // Load staging record + email preview
  useEffect(() => {
    if (!id) return
    const numId = parseInt(id, 10)
    const load = async () => {
      try {
        const [data, html] = await Promise.all([
          getFmvStaging(numId),
          getFmvStagingPreview(numId).catch(() => null),
        ])
        setStaging(data)
        setPreviewHtml(html)
        setCompanyId(data.companyId)
        setCompanyName(data.companyName || "")
        setCompanySearch(data.companyName || "")
        setFmvValue(data.fmvValue != null ? String(data.fmvValue) : "")
        setFmvDate(data.fmvDate || "")
        setFmvSource(data.fmvSource || "")
        setFileLabel(data.fileLabel || "")
        setFolderPath(data.folderPath || "")
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Failed to load staging record")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  // Company search with debounce
  useEffect(() => {
    if (!companySearch || companySearch.length < 2) {
      setCompanyResults([])
      return
    }

    const timer = setTimeout(async () => {
      try {
        const results = await searchCompaniesByName(companySearch)
        setCompanyResults(results.slice(0, 10))
      } catch {
        setCompanyResults([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [companySearch])

  // Auto-update file label when value/date/source change
  useEffect(() => {
    const parts = ["409A"]
    if (fmvDate) parts.push(`at ${fmvDate}`)
    if (fmvValue) parts.push(`is $${fmvValue}`)
    if (fmvSource) parts.push(`per ${fmvSource}`)
    if (staging?.senderName) parts.push(`FROM ${staging.senderName}`)
    setFileLabel(parts.join(" "))
  }, [fmvValue, fmvDate, fmvSource, staging?.senderName])

  const handleSelectCompany = useCallback((company: { id: number; name: string; folderPath?: string | null }) => {
    setCompanyId(company.id)
    setCompanyName(company.name)
    setCompanySearch(company.name)
    setShowCompanyDropdown(false)
    setFolderPath(company.folderPath || "")
  }, [])

  const handleConfirm = async () => {
    if (!companyId) {
      toaster.create({ title: "Please select a company", type: "error" })
      return
    }
    if (!fmvValue || isNaN(parseFloat(fmvValue))) {
      toaster.create({ title: "Please enter a valid FMV value", type: "error" })
      return
    }
    if (!fmvDate) {
      toaster.create({ title: "Please enter a date", type: "error" })
      return
    }

    setConfirming(true)
    try {
      await confirmFmvStaging(parseInt(id!, 10), {
        companyId,
        fmvValue: parseFloat(fmvValue),
        fmvDate,
        fmvSource,
        fileLabel,
        folderPath,
      })
      toaster.create({ title: "FMV uploaded and saved successfully", type: "success" })
      navigate("/core/fmv_review")
    } catch (err: any) {
      toaster.create({
        title: err?.response?.data?.error || err?.message || "Failed to confirm",
        type: "error",
      })
    } finally {
      setConfirming(false)
    }
  }

  if (loading) {
    return (
      <Box p={8} display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="lg" />
      </Box>
    )
  }

  if (error || !staging) {
    return (
      <Box p={8}>
        <Text color="red.400" fontSize="lg">{error || "Staging record not found"}</Text>
        <Button mt={4} onClick={() => navigate("/core/fmv_review")} variant="outline" size="sm">
          Back to FMV Review
        </Button>
      </Box>
    )
  }

  if (staging.status !== "pending") {
    return (
      <Box p={8}>
        <Text color="yellow.400" fontSize="lg">
          This staging record has already been {staging.status === "confirmed" ? "confirmed" : "processed"}.
        </Text>
        <Button mt={4} onClick={() => navigate("/core/fmv_review")} variant="outline" size="sm">
          Back to FMV Review
        </Button>
      </Box>
    )
  }

  return (
    <HStack align="stretch" h="calc(100vh - 64px)" gap={0}>
      {/* Left panel: Email preview */}
      <Box flex="3" borderRight="1px solid" borderColor="border.default" overflow="hidden">
        {previewHtml ? (
          <iframe
            srcDoc={previewHtml}
            title="Email Preview"
            style={{ width: "100%", height: "100%", border: "none" }}
            sandbox="allow-same-origin"
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" h="100%">
            <Text color="text.muted">No email preview available</Text>
          </Box>
        )}
      </Box>

      {/* Right panel: Form fields */}
      <Box flex="2" overflow="auto" p={6}>
        <Stack gap={5}>
          <Text fontSize="lg" fontWeight="600" color="text.default">
            Stage 409A FMV
          </Text>

          {/* Sender info (read-only) */}
          {staging.senderEmail && (
            <Box bg="card.bg" borderRadius="md" p={3} border="1px solid" borderColor="border.default">
              <Text fontSize="xs" color="text.muted" mb={1}>From</Text>
              <Text fontSize="sm">
                {staging.senderName && `${staging.senderName} — `}
                {staging.senderEmail}
              </Text>
              {staging.subject && (
                <>
                  <Text fontSize="xs" color="text.muted" mt={2} mb={1}>Subject</Text>
                  <Text fontSize="sm">{staging.subject}</Text>
                </>
              )}
            </Box>
          )}

          {/* Company selector */}
          <Box position="relative">
            <Text fontSize="xs" color="text.muted" mb={1}>Company</Text>
            <Input
              size="sm"
              value={companySearch}
              onChange={(e) => {
                setCompanySearch(e.target.value)
                setShowCompanyDropdown(true)
                if (e.target.value !== companyName) {
                  setCompanyId(null)
                }
              }}
              onFocus={() => setShowCompanyDropdown(true)}
              placeholder="Search company..."
              bg="card.bg"
              borderColor={companyId ? "green.600" : "border.default"}
            />
            {companyId && (
              <Text fontSize="xs" color="green.400" mt={1}>
                Selected: {companyName} (ID: {companyId})
              </Text>
            )}
            {showCompanyDropdown && companyResults.length > 0 && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={10}
                bg="card.bg"
                border="1px solid"
                borderColor="border.default"
                borderRadius="md"
                maxH="200px"
                overflow="auto"
                mt={1}
              >
                {companyResults.map((c) => (
                  <Box
                    key={c.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "hover.bg" }}
                    onClick={() => handleSelectCompany(c)}
                  >
                    <Text fontSize="sm">{c.name}</Text>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* 409A Value */}
          <Box>
            <Text fontSize="xs" color="text.muted" mb={1}>409A Value ($/share)</Text>
            <Input
              size="sm"
              type="number"
              step="0.01"
              value={fmvValue}
              onChange={(e) => setFmvValue(e.target.value)}
              placeholder="e.g. 12.50"
              bg="card.bg"
            />
          </Box>

          {/* 409A Date */}
          <Box>
            <Text fontSize="xs" color="text.muted" mb={1}>409A Date</Text>
            <Input
              size="sm"
              type="date"
              value={fmvDate}
              onChange={(e) => setFmvDate(e.target.value)}
              bg="card.bg"
            />
          </Box>

          {/* Source */}
          <Box>
            <Text fontSize="xs" color="text.muted" mb={1}>Source</Text>
            <Input
              size="sm"
              value={fmvSource}
              onChange={(e) => setFmvSource(e.target.value)}
              placeholder="e.g. Carta, Client email"
              bg="card.bg"
            />
          </Box>

          {/* File Label (auto-computed, editable) */}
          <Box>
            <Text fontSize="xs" color="text.muted" mb={1}>File Label</Text>
            <Input
              size="sm"
              value={fileLabel}
              onChange={(e) => setFileLabel(e.target.value)}
              bg="card.bg"
            />
          </Box>

          {/* Dropbox Folder Path */}
          <Box>
            <Text fontSize="xs" color="text.muted" mb={1}>Dropbox Folder</Text>
            <Input
              size="sm"
              value={folderPath}
              onChange={(e) => setFolderPath(e.target.value)}
              placeholder="/Company Name"
              bg="card.bg"
            />
            {!folderPath && (
              <Text fontSize="xs" color="yellow.500" mt={1}>
                No folder resolved — file won't be uploaded to Dropbox
              </Text>
            )}
          </Box>

          {/* Actions */}
          <HStack gap={3} mt={2}>
            <Button
              colorPalette="green"
              size="sm"
              onClick={handleConfirm}
              loading={confirming}
              disabled={!companyId || !fmvValue || !fmvDate}
            >
              Upload & Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/core/fmv_review")}
              disabled={confirming}
            >
              Cancel
            </Button>
          </HStack>
        </Stack>
      </Box>
    </HStack>
  )
}
